/**
 * `<Sheet />` e `<Modal />` — DESIGN_SYSTEM §4.3 e §7.4, sobre Radix Dialog (§10.2).
 *
 * Quando usar cada um (§7.4):
 *
 * | `Sheet` (sobe do fundo)                       | `Modal` (centro, alerta)                |
 * |-----------------------------------------------|-----------------------------------------|
 * | escolher filial, endereco, forma de pagamento | confirmar cancelamento de pedido        |
 * | filtros e ordenacao                           | confirmar exclusao de conta             |
 * | detalhe rapido de produto a partir da busca   | erro bloqueante (Pix expirou)           |
 * | explicar "como comprar item com receita"      | sessao expirada                         |
 * | aceitar/recusar substituto                    | —                                        |
 *
 * Regras comuns, entregues pelo Radix: `role="dialog"`, `aria-modal`, `aria-labelledby` no
 * titulo, trap de foco, `Esc` fecha, foco retorna ao disparador, `inert` no resto da pagina,
 * rolagem do corpo travada com preservacao da posicao.
 *
 * **Profundidade maxima de camada = 1** (§4.3). Um sheet nao abre outro sheet: navega DENTRO
 * do mesmo sheet, com "Voltar" no cabecalho e anuncio da mudanca de passo.
 */

import * as Dialog from '@radix-ui/react-dialog';
import type { ReactNode } from 'react';

import { t } from '../content';
import { Button } from './Button';
import { Icon } from './Icon';
import { cx } from './util';

export interface LayerBaseProps {
  readonly open: boolean;
  readonly onOpenChange: (aberto: boolean) => void;
  /** Obrigatorio: vira `aria-labelledby`. Camada sem nome acessivel e camada perdida. */
  readonly title: string;
  readonly description?: string;
  /** `false` so em fluxo de pagamento em curso (§4.3). */
  readonly dismissible?: boolean;
  /** Rodape fixo, com area segura. */
  readonly footer?: ReactNode;
  readonly children?: ReactNode;
  readonly className?: string;
}

export interface SheetProps extends LayerBaseProps {
  readonly size?: 'auto' | 'half' | 'full';
}

function Cabecalho({
  title,
  description,
  dismissible,
}: {
  readonly title: string;
  // `string | undefined` explicito, e nao opcional: com `exactOptionalPropertyTypes` o
  // repasse de um valor possivelmente ausente para uma prop opcional nao compila, e a
  // alternativa (montar o objeto condicionalmente) tornaria o JSX ilegivel por nada.
  readonly description: string | undefined;
  readonly dismissible: boolean;
}): ReactNode {
  return (
    <div className="balcao-layer__header">
      <div>
        <Dialog.Title className="balcao-layer__title">{title}</Dialog.Title>
        {description === undefined ? null : (
          <Dialog.Description className="balcao-layer__description">{description}</Dialog.Description>
        )}
      </div>
      {dismissible ? (
        // §7.4 — a alca de arraste e decorativa; existe SEMPRE um botao "Fechar" de 48 px.
        // §8.1, item 13: fechar sempre 48 px e com rotulo.
        <Dialog.Close asChild>
          <Button variant="ghost" size="md" aria-label={t('camada.fechar')}>
            <Icon name="x" size={24} />
          </Button>
        </Dialog.Close>
      ) : null}
    </div>
  );
}

export function Sheet({
  open,
  onOpenChange,
  title,
  description,
  dismissible = true,
  size = 'auto',
  footer,
  children,
  className,
}: SheetProps): ReactNode {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="balcao-scrim" />
        <Dialog.Content
          className={cx('balcao-sheet', size !== 'auto' && `balcao-sheet--${size}`, className)}
          onInteractOutside={(evento) => {
            if (!dismissible) evento.preventDefault();
          }}
          onEscapeKeyDown={(evento) => {
            if (!dismissible) evento.preventDefault();
          }}
        >
          <div className="balcao-sheet__grabber" aria-hidden />
          <Cabecalho title={title} description={description} dismissible={dismissible} />
          <div className="balcao-layer__body">{children}</div>
          {footer === undefined ? null : <div className="balcao-layer__footer">{footer}</div>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** §4.3 — `Modal` so nos tres `intent`. Fora deles, use `Sheet`. */
export type ModalIntent = 'confirm' | 'destructive' | 'blocking-error';

export interface ModalProps extends LayerBaseProps {
  readonly intent: ModalIntent;
}

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  dismissible = true,
  intent,
  footer,
  children,
  className,
}: ModalProps): ReactNode {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="balcao-scrim" />
        <Dialog.Content
          className={cx('balcao-modal', `balcao-modal--${intent}`, className)}
          onInteractOutside={(evento) => {
            if (!dismissible) evento.preventDefault();
          }}
          onEscapeKeyDown={(evento) => {
            if (!dismissible) evento.preventDefault();
          }}
          // §7.4 — o botao de confirmacao DESTRUTIVA nao e o padrao focado. O Radix foca o
          // primeiro elemento focavel; o `Content` recebe o foco e a ordem do rodape decide.
          // Por isso `ConfirmacaoDestrutiva` (§7.7) poe "Manter" antes de "Cancelar".
        >
          <Cabecalho title={title} description={description} dismissible={dismissible} />
          <div className="balcao-layer__body">{children}</div>
          {footer === undefined ? null : <div className="balcao-layer__footer">{footer}</div>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
